import React, { useEffect, useState } from 'react';

import { Button, Col, Drawer, Form, Pagination, Row, Spin } from 'antd';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';

import SelectDropdown from '../../../components/SelectDropdown/SelectDropdown';
import AgreementItem from '../../../components/TradeItem/AgreementItem';

import { ChevronLeft, ChevronRight, CloseIcon, Filter } from '../../../assets/icons';

import { getSubscriptionAgreements, resetSubscriptionAgreements } from '../../../store/reducer/accountStatementSlice';
import { formatDate, getStartEndDate } from '../../../utils/dateFormat.helper';
import { formatCurrency } from '../../../utils/usCurrencyFormatter';

const DURATIONS = ['Last 30 days', 'Last 60 days', 'Last 90 days', 'All time'];

const SubscriptionAgreements = () => {
  const [agreementDrawer, setAgreementDrawer] = useState(false);

  const [selectedDuration, setSelectedDuration] = useState('Last 30 days');

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);

  const dispatch = useDispatch();
  const { subscriptionAgreementsList } = useSelector((state) => state.accountStatements);

  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    duration: selectedDuration,
  });

  async function fetchSubscriptionAgreements() {
    const dates = getStartEndDate(filters.duration);
    try {
      setLoading(true);
      await dispatch(
        getSubscriptionAgreements({
          page: currentPage,
          limit: perPage,
          startDate: dates?.startDate ?? null,
          endDate: dates?.endDate ?? null,
        })
      );
    } catch (error) {
      // eslint-disable-next-line
      console.log({ error });
    } finally {
      setLoading(false);
    }
  }

  const onFetchDataDebounced = debounce(fetchSubscriptionAgreements, 0);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    onFetchDataDebounced();

    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [currentPage, perPage, filters.duration]);

  useEffect(() => {
    return () => {
      dispatch(resetSubscriptionAgreements());
    };
  }, []);

  return (
    <>
      <div className='data__header'>
        <h4 className='mb-8'>Subscription Agreements</h4>
        <p className='cursor-pointer filter-trigger' onClick={() => setAgreementDrawer(true)}>
          <Filter verticalAlign='-7px' /> <span className='fw-600'>Filters</span> <span>(1)</span>{' '}
        </p>
        <p className='data__header__description'>All subscription agreements will be displayed here once they become available.</p>
      </div>

      <div className='data__filters'>
        <Form>
          <Row gutter={16}>
            <Col span={8}>
              <SelectDropdown
                initialValue={selectedDuration}
                options={DURATIONS}
                classVal={'filter-select'}
                floatClassVal={'filter-float'}
                label={'Time Period'}
                id={'time-input'}
                inputId={'timePeriod'}
                name={'timePeriod'}
                onChangeSelect={(_, value) => {
                  setSelectedDuration(value);
                  setFilters({ ...filters, duration: value });
                }}
                alwaysSmall
              />
            </Col>
          </Row>
        </Form>
      </div>
      <div className='data__items mb-24'>
        <Spin spinning={loading}>
          {subscriptionAgreementsList?.subscriptionAgreementData?.length > 0 ? (
            <>
              <div className='data__items__head sub_agreement__head'>
                <p className='subhead-3'>Date</p>
                <p className='subhead-3'>Property Name</p>
                <p className='subhead-3'>Total Investment (USD)</p>
              </div>
              {subscriptionAgreementsList?.subscriptionAgreementData?.map((data) => {
                return (
                  <AgreementItem
                    key={data?.userFinancialDocId}
                    date={formatDate(data?.date)}
                    propertyName={data?.propertyName}
                    totalInvestment={formatCurrency(data?.principleAmount ?? 0)}
                    actionURL={data?.downloadUrl}
                  />
                );
              })}
            </>
          ) : (
            <>
              {!loading && !subscriptionAgreementsList?.subscriptionAgreementData?.length && (
                <div className='nodata'>
                  <p className='subhead-3 mb-4'>No subscription agreements found.</p>
                  <p className='mb-0'>All subscription agreements will appear here once available.</p>
                </div>
              )}
            </>
          )}
        </Spin>
      </div>
      {!!subscriptionAgreementsList?.totalCount && (
        <Pagination
          className='trade-pagination'
          prevIcon={<ChevronLeft color='#726460' size={20} verticalAlign='-5px' />}
          nextIcon={<ChevronRight color='#726460' size={20} verticalAlign='-5px' />}
          current={currentPage}
          pageSize={perPage}
          total={subscriptionAgreementsList?.totalCount}
          showLessItems
          showTotal={(total, range) => !!subscriptionAgreementsList?.totalCount && `${range[0]}-${range[1]} of ${total} results`}
          onChange={(page) => {
            setCurrentPage(page);
          }}
        />
      )}

      {agreementDrawer && (
        <Drawer
          className='filter-drawer'
          open={agreementDrawer}
          footer={false}
          onClose={() => setAgreementDrawer(false)}
          title={<p className='mb-0 subhead-3'>Filter Subscription Agreements</p>}
          closeIcon={<CloseIcon />}
          width='100%'
        >
          <Form className='filter-form'>
            <div className='filter-fields'>
              <SelectDropdown
                initialValue={selectedDuration}
                options={DURATIONS}
                classVal={'filter-select'}
                floatClassVal={'filter-float'}
                label={'Time Period'}
                id={'time-input'}
                inputId={'timePeriod'}
                name={'timePeriod'}
                onChangeSelect={(_, value) => setSelectedDuration(value)}
                alwaysSmall
              />
            </div>
            <Button
              type='primary'
              className='w-100'
              onClick={() => {
                setFilters({ duration: selectedDuration });
                setAgreementDrawer(false);
              }}
            >
              Apply Filters
            </Button>
          </Form>
        </Drawer>
      )}
    </>
  );
};

export default SubscriptionAgreements;
